import { Edit, Visibility, VisibilityOff } from '@mui/icons-material';
import { FormControlLabel, Grid2, Switch } from '@mui/material';
import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid';
import { Department } from '@thro-types/billing';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDepartmentMutation, useGetDepartmentsQuery } from 'api/hooks/billing/department';
import { CreateButton, DataGrid, TypedGridColumns } from 'app/components';
import { Searchfield } from 'app/components/Searchfield';
import { translations } from 'locales/i18n';
import { useTableQuery } from 'utils/hooks/useTableQuery';

import { DepartmentDrawer } from './DepartmentDrawer';

export const DepartmentSettings = () => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [showDisabled, setShowDisabled] = useState(false);

  const [department, setDepartment] = useState<Department | undefined>(undefined);

  const {
    search,
    handleSearchTextChange,
    debouncedSearchText,
    page,
    pageSize,
    sortingModel,
    handlePaginationChange,
    handleSortModelChange
  } = useTableQuery({
    defaultSort: [{ field: 'name', sort: 'asc' }],
    defaultPageSize: 100,
    sortKey: 'department_sort',
    paginationKeys: { page: 'department_page', pageSize: 'department_pageSize' }
  });

  const { data, isLoading, isFetching } = useGetDepartmentsQuery({
    params: {
      showDisabled: showDisabled || undefined,
      skip: page * pageSize,
      take: pageSize,
      sort: sortingModel[0]?.field,
      sortOrder: sortingModel[0]?.sort,
      search: debouncedSearchText
    }
  });

  const { update } = useDepartmentMutation();

  const removeFields = (department: Department) => {
    const omitFields = ['id', 'createdAt', 'updatedAt'] as const;
    omitFields.forEach(field => delete department[field]);
    return department;
  };

  const handleEnable = useCallback(
    async (department: Department) => {
      removeFields(department);
      await update.mutateAsync({
        key: department.departmentId,
        data: { ...department, isDisabled: false }
      });
    },
    [update]
  );

  const handleDisable = useCallback(
    async (department: Department) => {
      removeFields(department);
      await update.mutateAsync({
        key: department.departmentId,
        data: { ...department, isDisabled: true }
      });
    },
    [update]
  );

  const rowActions = useCallback(
    (params: GridRowParams<Department>) => {
      return [
        <GridActionsCellItem
          icon={<Edit />}
          label={t(translations.common.edit())}
          onClick={() => {
            setDepartment(params.row);
            setOpen(true);
          }}
          showInMenu
        />,
        params.row.isDisabled ? (
          <GridActionsCellItem
            icon={<Visibility sx={{ color: 'green' }} />}
            label={t(translations.common.activate())}
            onClick={() => {
              handleEnable(params.row);
            }}
            showInMenu
          />
        ) : (
          <GridActionsCellItem
            icon={<VisibilityOff sx={{ color: 'red' }} />}
            label={t(translations.common.deactivate())}
            onClick={() => {
              handleDisable(params.row);
            }}
            showInMenu
          />
        )
      ];
    },
    [t, handleDisable, handleEnable]
  );

  const columns = useMemo((): TypedGridColumns<Department> => {
    return [
      {
        field: 'name',
        flex: 2,
        headerName: t(translations.pages.billingSettings.department.name())
      },
      { field: 'email', flex: 2, headerName: t(translations.common.email()) },
      {
        field: 'phone',
        flex: 1,
        headerName: t(translations.common.phone()),
        sortable: false
      },
      {
        field: 'actions',
        flex: 1,
        minWidth: 100,
        headerName: t(translations.common.actions()),
        type: 'actions',
        getActions: rowActions
      }
    ];
  }, [t, rowActions]);

  return (
    <Grid2 style={{ flexGrow: 1 }}>
      <DepartmentDrawer open={open} setOpen={setOpen} department={department} />
      <Grid2 container justifyContent={'space-between'} paddingBottom={1}>
        <Searchfield handleSearch={handleSearchTextChange} search={search} />
        <Grid2>
          <FormControlLabel
            label={t(translations.pages.billingSettings.department.toggleText())}
            control={
              <Switch
                value={showDisabled}
                checked={showDisabled}
                onChange={e => setShowDisabled(e.target.checked)}
              />
            }
          ></FormControlLabel>
          <CreateButton
            renderSkeleton={isLoading}
            onClick={() => {
              setDepartment(undefined);
              setOpen(true);
            }}
          />
        </Grid2>
      </Grid2>
      <Grid2 container style={{ height: '80vh' }}>
        <DataGrid
          loading={isFetching}
          columns={columns}
          rows={data?.data}
          disableColumnResize
          disableColumnMenu
          disableRowSelectionOnClick
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={handlePaginationChange}
          sortingMode="server"
          sortModel={sortingModel}
          onSortModelChange={handleSortModelChange}
          rowCount={data?.count || 0}
        />
      </Grid2>
    </Grid2>
  );
};
